/**
 * Layout persistence behind a port (ADR-UI-001 decision 6, ADR-015).
 *
 * Layouts are non-sensitive presentation data, so the default implementation is
 * localStorage-backed. The port is async so a future server-side implementation
 * (M25 preference API) slots in behind the same interface without a call-site
 * change. Load returns the raw persisted value so migration stays centralised in
 * the store; NO sensitive operational data is ever written here.
 */
import { serializeLayout } from "./migration";
import type { WorkspaceLayout } from "./types";

export interface LayoutStore {
  /** Returns the raw persisted value (unknown, to be migrated) or `null`. */
  load(key: string): Promise<unknown>;
  save(key: string, layout: WorkspaceLayout): Promise<void>;
  clear(key: string): Promise<void>;
}

const PREFIX = "thw.workspace.layout.";

/** Default browser implementation. Fails soft when storage is unavailable. */
export const localStorageLayoutStore: LayoutStore = {
  load(key) {
    try {
      const raw = window.localStorage.getItem(`${PREFIX}${key}`);
      if (raw === null) return Promise.resolve(null);
      return Promise.resolve(JSON.parse(raw) as unknown);
    } catch {
      return Promise.resolve(null);
    }
  },
  save(key, layout) {
    try {
      window.localStorage.setItem(`${PREFIX}${key}`, serializeLayout(layout));
    } catch {
      // A layout that cannot be persisted must not break the live session.
    }
    return Promise.resolve();
  },
  clear(key) {
    try {
      window.localStorage.removeItem(`${PREFIX}${key}`);
    } catch {
      // Ignore: the in-memory reset already happened.
    }
    return Promise.resolve();
  },
};

/** In-memory implementation for tests, demos and SSR. */
export class InMemoryLayoutStore implements LayoutStore {
  private readonly records = new Map<string, string>();

  constructor(seed?: Readonly<Record<string, WorkspaceLayout>>) {
    if (seed !== undefined) {
      for (const [key, layout] of Object.entries(seed)) {
        this.records.set(key, serializeLayout(layout));
      }
    }
  }

  load(key: string): Promise<unknown> {
    const raw = this.records.get(key);
    if (raw === undefined) return Promise.resolve(null);
    return Promise.resolve(JSON.parse(raw) as unknown);
  }

  save(key: string, layout: WorkspaceLayout): Promise<void> {
    this.records.set(key, serializeLayout(layout));
    return Promise.resolve();
  }

  clear(key: string): Promise<void> {
    this.records.delete(key);
    return Promise.resolve();
  }

  /** Test helper: has anything been persisted under `key`? */
  has(key: string): boolean {
    return this.records.has(key);
  }
}
